export interface SSEEvent {
  type: string;
  content?: string;
}

export type EventHandler = (
  event: SSEEvent,
  options: any,
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (error: string) => void
) => void;

/**
 * SSE 事件分发器。
 *
 * 注意：_subThinkingBuffer/_subOutputBuffer 是实例级可变状态（跨 chunk 标签
 * 解析缓冲），因此每条 SSE 流必须使用独立实例（AgentService.chatStream 每次
 * 调用 new 一个）。多流共用同一实例会导致标签缓冲互相拼接（内容串流）。
 * handler 注册为纯函数，按实例新建无共享状态风险，开销可忽略。
 */
export class SSEEventDispatcher {
  private handlers: Map<string, EventHandler> = new Map();
  // P-F1: 流式标签缓冲 —— 子智能体内容可能跨多个 SSE chunk 到达，
  // 此时起始标签与闭合标签不在同一个 event.content 中，正则匹配会失败。
  private _subThinkingBuffer = '';
  private _subOutputBuffer = '';

  constructor() {
    this.registerDefaultHandlers();
  }

  private registerDefaultHandlers(): void {
    this.register('replace', this.handleReplace);
    this.register('text', this.handleText);
    this.register('thinking', this.handleThinking);
    this.register('status', this.handleStatus);
    this.register('intent', this.handleIntent);
    this.register('message_ids', this.handleMessageIds);
    this.register('agent_id', this.handleAgentId);
    this.register('collab_status', this.handleCollabStatus);
    this.register('sub_thinking', this.handleSubThinking);
    this.register('sub_output', this.handleSubOutput);
    // SOFT-2 清理（2026-07-24）：已删除 clarification 事件注册（后端从不发送此事件）
    // P2-8/9 清理（2026-07-24）：已删除 generation_confirm 事件注册（已由 task_ask 统一替代）
    this.register('search_result', this.handleSearchResult);
    // 任务流改版：新增事件类型
    this.register('task_stage', this.handleTaskStage);
    this.register('task_intro', this.handleTaskIntro);
    this.register('task_ask', this.handleTaskAsk);
    // 画布节点链路（§5.7 P1-2）：工作流终态完结事件（persist_rules 落库键值）
    this.register('skill_result', this.handleSkillResult);
    // 中断决策方案（附录 C）：决策透明化事件
    this.register('interrupt_received', this.handleInterruptReceived);
    this.register('interrupt_decision', this.handleInterruptDecision);
    this.register('task_plan', this.handleTaskPlan);
    // P1 工具化（10.5）：工具过程状态条（tool_call 显示进行中 / tool_result 消失或失败）
    this.register('tool_status', this.handleToolStatus);
    // 交互改版（2026-10-14）：尾部轻提醒帧（未完成任务 + 继续按钮）
    this.register('task_reminder', this.handleTaskReminder);
    // 放弃状态回写（2026-10-15）：discard_task 终态帧（卡片原位显示已放弃）
    this.register('task_reminder_update', this.handleTaskReminderUpdate);
    // 🔴 G2-2 修复: 心跳不再 no-op——后端长静默期（路由/意图解析/长工具
    // 调用）每 3s 一跳，是连接存活的唯一信号。透传 onHeartbeat 供容器
    // 活跃度刷新（FE-5 看门狗/FE-2 死容器判据共用），否则连接活着也
    // 会被误判死亡（看门狗误杀前台流 / 切回时容器误降级 DB 路径）。
    this.register('heartbeat', (_event, options) => {
      options?.onHeartbeat?.();
    });
    this.register('error', this.handleError);
    this.register('done', this.handleDone);
  }

  register(eventType: string, handler: EventHandler): void {
    this.handlers.set(eventType, handler);
  }

  dispatch(
    event: SSEEvent,
    options: any,
    onChunk: (text: string) => void,
    onDone: () => void,
    onError: (error: string) => void
  ): void {
    const handler = this.handlers.get(event.type);
    if (handler) {
      handler(event, options, onChunk, onDone, onError);
    } else {
      // A3: 未知事件类型只记录调试警告，不报错。便于排查后端新增事件后
      // 前端忘记注册 handler 的情况。
      console.warn(
        `[SSEEventDispatcher] Unknown SSE event type: "${event.type}", ` +
        `registered events: [${Array.from(this.handlers.keys()).join(', ')}]`
      );
    }
  }

  private handleReplace: EventHandler = (event, _, onChunk) => {
    onChunk('__REPLACE__');
  };

  private handleText: EventHandler = (event, _, onChunk) => {
    if (!event.content) return;
    // 防御（2026-10-16）：text content 恒应为字符串。旧后端会把恰好合法的
    // JSON 标量 token（如独立数字 "2026"）误解析成数值——JSON.stringify 与其
    // 互逆可无损还原原文，避免 onChunk 收到非字符串导致 chunk.includes TypeError
    onChunk(typeof event.content === 'string' ? event.content : JSON.stringify(event.content));
  };

  private handleThinking: EventHandler = (event, options) => {
    if (!event.content) return;
    // 同 handleText 防御：thinking 内容恒为字符串（LLM 推理 token 原文）
    const content = typeof event.content === 'string'
      ? event.content
      : JSON.stringify(event.content);
    options?.onThinking?.(content);
  };

  private handleStatus: EventHandler = (event, options) => {
    if (event.content) {
      options?.onStatus?.(event.content);
    }
  };

  private handleIntent: EventHandler = (event, options) => {
    if (!event.content) return;

    try {
      const content = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onIntent?.(content);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse intent content:', e);
    }
  };

  private handleMessageIds: EventHandler = (event, options) => {
    if (!event.content) return;

    try {
      const content = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content || '{}');
      options?.onMessageIds?.(content);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse message_ids content:', e);
    }
  };

  private handleAgentId: EventHandler = (event, options) => {
    if (event.content) {
      options?.onAgentId?.(event.content);
    }
  };

  private handleCollabStatus: EventHandler = (event, options) => {
    if (event.content) {
      options?.onCollabStatus?.(event.content);
    }
  };

  /**
   * P-F1: 流式标签解析通用方法。
   * 思路：
   *   1) 先与缓冲区已有内容合并；
   *   2) 从头查找首个 [TAG:xxx] 起始标签；
   *   3) 在合并内容中查找配对的 [/TAG:xxx] 闭合标签；
   *   4) 找到则提取并回调，剩余未闭合部分回到缓冲区等待后续 chunk。
   */
  private _handleBufferedTag(
    tagName: 'SUB_THINKING' | 'SUB_OUTPUT',
    bufferField: '_subThinkingBuffer' | '_subOutputBuffer',
    content: string,
    callback: (agentId: string, body: string) => void,
  ): void {
    const text = (this[bufferField] + content).replace(/\r/g, '');
    const openRe = new RegExp(`\\[${tagName}:([^\\]]+)\\]`);
    const closeTagHead = `[/${tagName}:`;

    let remaining = text;

    // 循环：一次 chunk 可能含多对标签
    while (remaining.length > 0) {
      const openMatch = remaining.match(openRe);
      if (!openMatch || openMatch.index === undefined) {
        // 没有起始标签：如果 buffer 之前有遗留，说明标签被截断等待；
        // 如果 buffer 原本为空，说明这段内容不包含目标标签，整体丢弃。
        this[bufferField] = remaining.includes(`[${tagName}:`) ? remaining : '';
        return;
      }

      const agentId = openMatch[1];
      const bodyStart = openMatch.index + openMatch[0].length;
      const closeSearchFrom = remaining.indexOf(closeTagHead + agentId + ']', bodyStart);

      if (closeSearchFrom === -1) {
        // 有起始但无闭合：整段内容进入缓冲区，等下一个 chunk 到齐后再处理
        this[bufferField] = remaining;
        return;
      }

      const body = remaining.slice(bodyStart, closeSearchFrom);
      callback(agentId, body);

      // 移动 remaining 到闭合标签之后
      const closeEnd = closeSearchFrom + (closeTagHead + agentId + ']').length;
      remaining = remaining.slice(closeEnd);
    }

    this[bufferField] = '';
  }

  private handleSubThinking: EventHandler = (event, options) => {
    if (!event.content) return;
    this._handleBufferedTag(
      'SUB_THINKING',
      '_subThinkingBuffer',
      event.content,
      (agentId, body) => options?.onSubThinking?.(agentId, body),
    );
  };

  private handleSubOutput: EventHandler = (event, options) => {
    if (!event.content) return;
    this._handleBufferedTag(
      'SUB_OUTPUT',
      '_subOutputBuffer',
      event.content,
      (agentId, body) => options?.onSubOutput?.(agentId, body),
    );
  };

  // SOFT-2 清理（2026-07-24）：已删除 handleClarification（clarification 事件后端从不发送）
  // P2-8/9 清理（2026-07-24）：已删除 handleGenerationConfirm（generation_confirm 事件已由 task_ask 统一替代）

  private handleSearchResult: EventHandler = (event, options) => {
    if (!event.content) return;

    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onSearchResult?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse search_result content:', e);
    }
  };

  // ===== 任务流改版：新增事件处理器 =====

  private handleTaskStage: EventHandler = (event, options) => {
    if (!event.content) return;
    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onTaskStage?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse task_stage content:', e);
    }
  };

  private handleTaskIntro: EventHandler = (event, options) => {
    if (!event.content) return;
    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onTaskIntro?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse task_intro content:', e);
    }
  };

  private handleTaskAsk: EventHandler = (event, options) => {
    if (!event.content) return;
    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onTaskAsk?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse task_ask content:', e);
    }
  };

  private handleSkillResult: EventHandler = (event, options) => {
    if (!event.content) return;
    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onSkillResult?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse skill_result content:', e);
    }
  };

  // ===== 中断决策方案（附录 C）：新增事件处理器 =====

  private handleInterruptReceived: EventHandler = (event, options) => {
    if (!event.content) return;
    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onInterruptReceived?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse interrupt_received content:', e);
    }
  };

  private handleInterruptDecision: EventHandler = (event, options) => {
    if (!event.content) return;
    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onInterruptDecision?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse interrupt_decision content:', e);
    }
  };

  private handleTaskPlan: EventHandler = (event, options) => {
    if (!event.content) return;
    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onTaskPlan?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse task_plan content:', e);
    }
  };

  // P1 工具化（10.5）：__TOOL__:{json} 工具过程状态帧
  private handleToolStatus: EventHandler = (event, options) => {
    if (!event.content) return;
    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onToolStatus?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse tool_status content:', e);
    }
  };

  // 交互改版（2026-10-14）：__TASK_REMINDER__:{json} 尾部轻提醒帧
  private handleTaskReminder: EventHandler = (event, options) => {
    if (!event.content) return;
    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onTaskReminder?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse task_reminder content:', e);
    }
  };

  // 放弃状态回写（2026-10-15）：__TASK_REMINDER_UPDATE__:{json} discard_task 终态帧
  private handleTaskReminderUpdate: EventHandler = (event, options) => {
    if (!event.content) return;
    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onTaskReminderUpdate?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse task_reminder_update content:', e);
    }
  };

  private handleError: EventHandler = (event, _, __, ___, onError) => {
    onError(event.content || 'Unknown error');
  };

  private handleDone: EventHandler = (_, __, ___, onDone) => {
    onDone();
  };
}

// 全局单例：仅供单元测试使用（tests/test-sse-dispatcher.ts）。
// 生产代码（AgentService.chatStream）必须按流新建实例，禁止使用本单例
// （见类注释——标签缓冲为实例可变状态，跨流共享会串流）。
export const sseEventDispatcher = new SSEEventDispatcher();
